import { useState, useEffect, memo } from 'react';
import type { FC } from 'react';
import { Modal, Input } from 'antd';
import type { CameraSettings } from '../../types';
import { useIsMobile } from '../../hooks/use-media-query';

export interface CounterSettingsModalProps {
  open: boolean;
  cameraName: string;
  initialValues?: CameraSettings;
  onSave: (values: CameraSettings) => void;
  onCancel: () => void;
  loading?: boolean;
}

export const CounterSettingsModal: FC<CounterSettingsModalProps> = memo(
  ({ open, cameraName: initialCameraName, initialValues, onCancel, onSave, loading = false }) => {
    const isMobile = useIsMobile();
    const [cameraName, setCameraName] = useState(initialValues?.cameraName || initialCameraName);
    const [weightPerFish, setWeightPerFish] = useState(
      initialValues?.weightPerFish?.toString() || ''
    );
    const [inputTank, setInputTank] = useState(initialValues?.inputTank || '');
    const [outputTank, setOutputTank] = useState(initialValues?.outputTank || '');

    // Reset values when modal is opened or when tank values change
    useEffect(() => {
      if (open && initialValues) {
        setCameraName(initialValues.cameraName);
        setWeightPerFish(initialValues.weightPerFish?.toString() || '');
        setInputTank(initialValues.inputTank || '');
        setOutputTank(initialValues.outputTank || '');
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps -- Reset form when modal opens or tank values update from server
    }, [open, initialValues?.inputTank, initialValues?.outputTank]);

    const handleSave = () => {
      if (!cameraName.trim()) return;

      const weight = parseFloat(weightPerFish);
      const validWeight = !isNaN(weight) && weight > 0 ? weight : undefined;

      onSave({
        cameraName: cameraName.trim(),
        weightPerFish: validWeight,
        inputTank: inputTank.trim() || undefined,
        outputTank: outputTank.trim() || undefined,
      });
    };

    const handleCancel = () => {
      // Reset to initial values
      if (initialValues) {
        setCameraName(initialValues.cameraName);
        setWeightPerFish(initialValues.weightPerFish?.toString() || '');
        setInputTank(initialValues.inputTank || '');
        setOutputTank(initialValues.outputTank || '');
      }
      onCancel();
    };

    return (
      <Modal
        open={open}
        onCancel={handleCancel}
        footer={null}
        centered
        width={400}
        closable={false}
        styles={{
          body: {
            padding: '8px 0',
            backgroundColor: '#FFFFFF',
          },
          content: {
            backgroundColor: '#FFFFFF',
            paddingTop: '0px',
            paddingBottom: '0px',
            borderRadius: '8px',
          },
        }}
      >
        <div
          className={`py-2 ${isMobile ? 'flex flex-col h-full' : ''}`}
          style={isMobile ? { minHeight: '100%' } : {}}
        >
          {/* Header */}
          {isMobile ? (
            <div className="mb-4 px-1">
              <h2 className="text-[12px] font-semibold text-gray-900">Counter Settings</h2>
            </div>
          ) : (
            <div className="flex items-center justify-between mb-4 px-1">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="text-blue-500 hover:text-blue-600 font-bold text-base disabled:opacity-50"
                style={{
                  color: '#007AFF',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Cancel
              </button>
              <h2 className="text-[12px] font-semibold text-gray-900">Counter Settings</h2>
              <button
                onClick={handleSave}
                disabled={loading}
                className="text-blue-500 hover:text-blue-600 font-bold text-base disabled:opacity-50"
                style={{
                  color: '#007AFF',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}

          {/* Form */}
          <div className={`space-y-4 ${isMobile ? 'flex-1' : ''}`}>
            {/* About Section */}
            <div>
              <label
                className="block text-sm font-bold text-gray-500 mb-2"
                style={{ fontSize: '12px', color: '#8E8E93' }}
              >
                About
              </label>
              <div className="space-y-2">
                <div style={{ position: 'relative' }}>
                  <label
                    className="block text-sm text-gray-700 mb-1 font-semibold"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#2C2C2E',
                      pointerEvents: 'none',
                      zIndex: 1,
                      fontSize: '12px',
                    }}
                  >
                    Camera Name
                  </label>
                  <Input
                    value={cameraName}
                    onChange={(e) => setCameraName(e.target.value)}
                    placeholder="CAMERA-01"
                    size="large"
                    disabled={loading}
                    style={{
                      backgroundColor: '#F5F5F5',
                      border: 'none',
                      borderRadius: '16px',
                      textAlign: 'right',
                      color: '#8E8E93',
                      paddingRight: '12px',
                      fontSize: '12px',
                    }}
                  />
                </div>
                <label
                  className="block text-sm font-bold text-gray-500 mb-2"
                  style={{ fontSize: '12px', color: '#8E8E93' }}
                >
                  Input Tank
                </label>
                <div style={{ position: 'relative' }}>
                  <label
                    className="block text-sm text-gray-700 mb-1 font-semibold"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#2C2C2E',
                      pointerEvents: 'none',
                      zIndex: 1,
                      fontSize: '12px',
                    }}
                  >
                    Tank Name
                  </label>
                  <Input
                    value={inputTank}
                    onChange={(e) => setInputTank(e.target.value)}
                    placeholder="Tank Name"
                    size="large"
                    disabled={loading}
                    style={{
                      backgroundColor: '#F5F5F5',
                      border: 'none',
                      borderRadius: '16px',
                      textAlign: 'right',
                      color: '#8E8E93',
                      paddingRight: '12px',
                      fontSize: '12px',
                    }}
                  />
                </div>
                <label
                  className="block text-sm font-bold text-gray-500 mb-2"
                  style={{ fontSize: '12px', color: '#8E8E93' }}
                >
                  Output Tank
                </label>
                <div style={{ position: 'relative' }}>
                  <label
                    className="block text-sm text-gray-700 mb-1 font-semibold"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#2C2C2E',
                      pointerEvents: 'none',
                      zIndex: 1,
                      fontSize: '12px',
                    }}
                  >
                    Tank Name
                  </label>
                  <Input
                    value={outputTank}
                    onChange={(e) => setOutputTank(e.target.value)}
                    placeholder="Tank Name"
                    size="large"
                    disabled={loading}
                    style={{
                      backgroundColor: '#F5F5F5',
                      border: 'none',
                      borderRadius: '16px',
                      textAlign: 'right',
                      color: '#8E8E93',
                      paddingRight: '12px',
                      fontSize: '12px',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Weight Calculation Section */}
            <div>
              <label
                className="block text-sm font-bold text-gray-500 mb-2"
                style={{ fontSize: '12px', color: '#8E8E93' }}
              >
                Weight Calculation
              </label>
              <div className="space-y-2">
                <div style={{ position: 'relative' }}>
                  <label
                    className="block text-sm text-gray-700 mb-1 font-semibold"
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#2C2C2E',
                      pointerEvents: 'none',
                      zIndex: 1,
                      fontSize: '12px',
                    }}
                  >
                    Weight per fish (kg)
                  </label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={weightPerFish}
                    onChange={(e) => setWeightPerFish(e.target.value)}
                    placeholder=""
                    size="large"
                    disabled={loading}
                    style={{
                      backgroundColor: '#F5F5F5',
                      border: 'none',
                      borderRadius: '16px',
                      textAlign: 'right',
                      color: '#8E8E93',
                      paddingRight: '12px',
                      fontSize: '12px',
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: Buttons at bottom right */}
          {isMobile && (
            <div className="flex items-center justify-end gap-4 mt-6 px-1">
              <button
                onClick={handleCancel}
                disabled={loading}
                className="text-blue-500 hover:text-blue-600 font-bold text-base disabled:opacity-50"
                style={{
                  color: '#007AFF',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="text-blue-500 hover:text-blue-600 font-bold text-base disabled:opacity-50"
                style={{
                  color: '#007AFF',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </div>
      </Modal>
    );
  }
);

CounterSettingsModal.displayName = 'CounterSettingsModal';
